import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { MessageModal } from './MessageModal';
interface AddPlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
}
export function AddPlotModal({
  isOpen,
  onClose,
  projectName
}: AddPlotModalProps) {
  const [plotData, setPlotData] = useState({
    plotNumber: '',
    size: '',
    facing: '',
    price: '',
    status: 'available'
  });
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!plotData.plotNumber || !plotData.size || !plotData.price) {
      alert('Please fill all required fields');
      return;
    }
    setIsMessageModalOpen(true);
    setTimeout(() => {
      onClose();
      setPlotData({
        plotNumber: '',
        size: '',
        facing: '',
        price: '',
        status: 'available'
      });
    }, 2000);
  };
  return <>
      <div data-testid="add-plot-modal-overlay" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-354">
        <div data-testid="add-plot-modal" className="bg-white border border-gray-300 max-w-lg w-full shadow-lg" data-id="element-355">
          <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-356">
            <h3 data-testid="add-plot-modal-title" className="font-bold text-lg flex items-center gap-2" data-id="element-357">
              <Plus className="w-5 h-5" data-id="element-358" />
              Add New Plot
            </h3>
            <button data-testid="add-plot-modal-close-btn" onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-359">
              <X className="w-5 h-5" data-id="element-360" />
            </button>
          </div>

          <form data-testid="add-plot-form" onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-361">
            <div data-testid="add-plot-project-info" className="p-3 bg-gray-50 border border-gray-200" data-id="element-362">
              <p className="text-sm text-gray-600" data-id="element-363">Project:</p>
              <p data-testid="add-plot-project-name" className="font-bold" data-id="element-364">
                {projectName}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-365">
              <div data-id="element-366">
                <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-367">
                  Plot Number *
                </label>
                <input data-testid="add-plot-number-input" type="text" required value={plotData.plotNumber} onChange={e => setPlotData({
                ...plotData,
                plotNumber: e.target.value
              })} placeholder="e.g., A-101" className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-368" />
              </div>

              <div data-id="element-369">
                <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-370">
                  Size (Sq. Ft) *
                </label>
                <input data-testid="add-plot-size-input" type="number" required value={plotData.size} onChange={e => setPlotData({
                ...plotData,
                size: e.target.value
              })} placeholder="2400" className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-371" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-372">
              <div data-id="element-373">
                <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-374">
                  Facing
                </label>
                <select data-testid="add-plot-facing-select" value={plotData.facing} onChange={e => setPlotData({
                ...plotData,
                facing: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-375">
                  <option value="" data-id="element-376">Select</option>
                  <option value="North" data-id="element-377">North</option>
                  <option value="South" data-id="element-378">South</option>
                  <option value="East" data-id="element-379">East</option>
                  <option value="West" data-id="element-380">West</option>
                  <option value="North-East" data-id="element-381">North-East</option>
                  <option value="North-West" data-id="element-382">North-West</option>
                  <option value="South-East" data-id="element-383">South-East</option>
                  <option value="South-West" data-id="element-384">South-West</option>
                </select>
              </div>

              <div data-id="element-385">
                <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-386">
                  Price (Lakhs) *
                </label>
                <input data-testid="add-plot-price-input" type="number" required step="0.01" value={plotData.price} onChange={e => setPlotData({
                ...plotData,
                price: e.target.value
              })} placeholder="45" className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-387" />
              </div>
            </div>

            <div data-id="element-388">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-389">
                Status
              </label>
              <select data-testid="add-plot-status-select" value={plotData.status} onChange={e => setPlotData({
              ...plotData,
              status: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-390">
                <option value="available" data-id="element-391">Available</option>
                <option value="reserved" data-id="element-392">Reserved</option>
                <option value="sold" data-id="element-393">Sold</option>
                <option value="blocked" data-id="element-394">Blocked</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4" data-id="element-395">
              <button data-testid="add-plot-cancel-btn" type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-396">
                Cancel
              </button>
              <button data-testid="add-plot-submit-btn" type="submit" className="px-4 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-397">
                Add Plot
              </button>
            </div>
          </form>
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title="Plot Added" message={`Plot ${plotData.plotNumber} has been added to ${projectName} successfully.`} type="success" data-id="element-398" />
    </>;
}